import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName, IconPrefix } from "@fortawesome/fontawesome-svg-core";
import cn from "classnames";
import React, { ReactNode, useMemo } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { NavLink, UncontrolledTooltip } from "reactstrap";
import { useBreakpointBelowLg } from "../../../hooks/useBreakpointBelowLg";
import { useGlobalCore } from "../../../cores/globals";
import { Icon } from "../companies/icons/Icon";
import { showSidebarDisabledToast } from "./sidebarDisabledMessages";

export const NavItemGroup: React.FC<{
  groupId: string;
  children: ReactNode;
  label: string;
  iconName?: IconName;
  iconPrefix?: IconPrefix;
  activeUrls?: string[];
  disabled?: boolean;
  disabledMessage?: string;
}> = ({
  children,
  groupId,
  label,
  iconName,
  iconPrefix,
  activeUrls,
  disabled,
  disabledMessage,
}) => {
  const { pathname } = useLocation();
  const { collapseSideBar, sidebarActiveGroups, setSidebarGroup } =
    useGlobalCore();
  const isMobileSidebar = useBreakpointBelowLg();
  const isCollapsedDesktop = collapseSideBar && !isMobileSidebar;
  const showGroupLabel = !collapseSideBar || isMobileSidebar;

  const open = sidebarActiveGroups.entities[groupId] ? true : false;
  const active = useMemo(
    () =>
      activeUrls?.some(
        (url) =>
          pathname === url ||
          !!matchPath(pathname, { path: url, exact: true })
      ),
    [activeUrls, pathname]
  );

  return (
    <div
      id={groupId}
      className={cn(" mb-2 sidebar-item", {
        "active-group": active,
        pointer: !disabled,
        "sidebar-item--disabled": disabled,
      })}
    >
      <NavLink
        onClick={() => {
          if (disabled) {
            showSidebarDisabledToast(
              disabledMessage,
              `sidebar-group-disabled-${groupId}`
            );
            return;
          }
          setSidebarGroup({
            id: groupId,
          });
        }}
        className={cn("sidebar-group-link align-items-center", {
          disabled,
          "sidebar-link-collapsed": isCollapsedDesktop,
        })}
      >
        <span className="sidebar-group-link-main d-flex align-items-center min-w-0">
          <span className="sidebar-icon-slot flex-shrink-0">
            {iconName && (
              <Icon className="group-icon" name={iconName} prefix={iconPrefix} />
            )}
          </span>
          {showGroupLabel && (
            <span className="ml-3 group-label text-truncate">{label}</span>
          )}
        </span>
        {showGroupLabel && !isCollapsedDesktop && (
          <FontAwesomeIcon
            icon={faChevronDown}
            className={cn("sidebar-group-chevron flex-shrink-0", {
              "sidebar-group-chevron--open": open,
            })}
            aria-hidden
          />
        )}
      </NavLink>
      {open && <div className="pl-3">{children}</div>}
      {disabledMessage && disabled && (
        <UncontrolledTooltip
          placement="right"
          target={groupId}
          trigger="hover"
          fade={false}
          autohide
          popperClassName="sidebar-disabled-tooltip"
        >
          {disabledMessage}
        </UncontrolledTooltip>
      )}
    </div>
  );
};
